import React, { useEffect, useRef, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  FormInput,
  Icon,
  MoreOptions,
} from "../../../components/Component";
import { TimesheetStatusType } from "../../../utils";
import { formatDateToString, openLinkInNewTab, toCapitalize } from "../../../utils/Functions";
import { ButtonGroup } from "reactstrap";
import DateRangePicker from "../../../components/input/date-time/DateRangePicker";
import { exportTimesheet } from "./TimesheetData";
import Timesheets from "./Timesheets";
import { UserActionType, UserPermissionType, isAllowToAccess } from "../../../services/permission/permission";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import SendNotificationModal from "./SendNotificationModal";
import {
  ClientTypeDropdownOptions,
  parseUrlParams,
  removeWhitespace,
  SendNotificationType,
  updateHash,
} from "../../../utils/Utils";
import HospitalHomeCareDropDown from "../../../components/dropdowns/HospitalHomeCareDropDown";

const TimesheetNew = () => {
  const [loadingExport, setLoadingExport] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sm, updateSm] = useState(false);
  const [status, setStatus] = useState({
    value: TimesheetStatusType.all,
  });
  const [carer, setCarer] = useState();
  const [client, setClient] = useState();
  const [activeButton, setActiveButton] = useState(0); // 0 corresponds to the index of the first button
  const [isSwitchOn, setSwitchOn] = useState(false);
  const [totalItem, setTotalItems] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [notificationModal, setNotificationModal] = useState(false);
  const [clientType, setClientType] = useState();
  const [currentPage, setCurrentPage] = useState();

  const timesheetRef = useRef(null);
  const location = useLocation();
  const weekdata = location?.state?.data;
  const params = parseUrlParams();

  useEffect(() => {
    if (params.clientType) {
      const selectedClientType = ClientTypeDropdownOptions.find(
        (option) => removeWhitespace(option.value) === removeWhitespace(params.clientType)
      );
      if (selectedClientType && selectedClientType.value !== clientType?.value) {
        setClientType(selectedClientType);
      }
    }

    if (params.status) {
      const selectedStatus = timesheetStatus.find(
        (option) => removeWhitespace(option) === removeWhitespace(params.status)
      );
      if (selectedStatus && selectedStatus !== status?.value) {
        handleButtonClick(selectedStatus);
      }
    }

    if (params.calendarView) {
      if (removeWhitespace(params.calendarView) === removeWhitespace("ALL")) {
        setSwitchOn(true);
        updateQueryParams({ calendarView: "ALL", startDate: "", endDate: "" });
      } else {
        setSwitchOn(false);
      }
    }

    if (params.date) {
      const decodedDate = decodeURIComponent(params.date);
      const [startDateFormatted, endDateFormatted] = decodedDate.split("_to_");

      const newStartDate = new Date(startDateFormatted);
      const newEndDate = new Date(endDateFormatted);

      // Format the dates to YYYY-MM-DD
      const startDateFormattedString = formatDateToString(newStartDate);
      const endDateFormattedString = formatDateToString(newEndDate);

      setStartDate(startDateFormattedString);
      setEndDate(endDateFormattedString);
    } else if (location?.state?.isMoveTimesheet) {
      setStartDate(weekdata?.startDate);
      setEndDate(weekdata?.endDate);
    }

    if (params.search) {
      const searchQuery = decodeURIComponent(params.search);
      setSearchText(searchQuery);
    }

    if (params.page) {
      setCurrentPage(parseInt(params.page, 10));
    } else {
      setCurrentPage(1);
    }
  }, [location]);

  useEffect(() => {
    if (!currentPage) return;
    updateQueryParams({ page: currentPage });
  }, [currentPage]);

  function exportTimesheetList(ev) {
    if (!startDate || !endDate) return;
    setLoadingExport(true);
    let params = {
      // rotaId: 0,
      startDate: !isSwitchOn ? startDate : "",
      endDate: !isSwitchOn ? endDate : "",
      clientId: client?.id,
      carerId: carer?.id,
      status: status?.value === TimesheetStatusType.all ? undefined : status?.value,
      search: searchText,
    };

    exportTimesheet(params, (data, error) => {
      if (error === null) {
        openLinkInNewTab(data.fileUrl, ev);
      }
      setLoadingExport(false);
    });
  }

  const val = {
    ...TimesheetStatusType,
  };

  const timesheetStatus = Object.values(val);

  const updateQueryParams = (updates) => {
    if (!isSwitchOn && !startDate && !endDate && !updates.startDate && !updates.endDate) return;
    if (isSwitchOn && currentPage !== updates.page) return;
    updateHash({
      page: currentPage,
      searchText: searchText,
      clientType: clientType?.value || updates.clientType || params.clientType,
      status: status?.value || updates.status,
      calendarView: !isSwitchOn ? "Week" : "ALL",
      startDate: !isSwitchOn ? startDate : "",
      endDate: !isSwitchOn ? endDate : "",
      pathname: location.pathname,
      ...updates,
    });
  };

  const handleButtonClick = (item) => {
    const newValue = item === "ALL" ? "" : item;
    setActiveButton(timesheetStatus.findIndex((valueItem) => valueItem === item));
    setStatus({ value: newValue });
    updateQueryParams({ status: newValue });
  };

  const handleSwitchToggle = () => {
    setSwitchOn(!isSwitchOn);
    setCurrentPage(1);
    if (isSwitchOn) {
      setStartDate("");
      setEndDate("");
    }
    const newCalendarView = !isSwitchOn ? "ALL" : "Week";
    updateQueryParams({ calendarView: newCalendarView, page: 1 });
  };

  const onSearchChange = (searchText) => {
    setSearchText(searchText);
    updateQueryParams({ searchText: searchText });
  };

  const onHospitalTypeChange = (clientType) => {
    setClientType(clientType);
    updateQueryParams({ clientType: clientType?.value });
  };

  const getTotalItem = (totalItem) => {
    // the callback. Use a better name
    setTotalItems(totalItem);
  };

  const handleDatePickerChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    updateQueryParams({ startDate: newStartDate, endDate: newEndDate });
  };

  return (
    <React.Fragment>
      <Head title="Timesheet" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="flex-wrap">
            <BlockHeadContent>
              <div className="d-flex align-items-center">
                <span className="me-2">
                  <BlockTitle page>Timesheet</BlockTitle>
                  <BlockDes>{totalItem} Timesheets</BlockDes>
                </span>
              </div>
            </BlockHeadContent>
            <BlockHeadContent className="ms-auto">
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="more-v"></Icon>
                </Button>
                <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                  <ul className="nk-block-tools g-2 d-flex flex-wrap justify-content-end">
                    <li>
                      <FormInput
                        isShowLabel={false}
                        value={searchText ?? ""}
                        placeholder="Request ID, Client/Carer, Ward"
                        onTextChange={onSearchChange}
                      />
                    </li>
                    <li>
                      <HospitalHomeCareDropDown
                        value={clientType}
                        placeholder={"Client Type"}
                        className="w-125px"
                        onSelect={(e) => {
                          onHospitalTypeChange(e);
                        }}
                      />
                    </li>
                    <li>
                      <ButtonGroup>
                        {timesheetStatus
                          // ?.filter((item) => item !== TimesheetStatusType.finalised)
                          .map((item, index) => (
                            <Button
                              key={index}
                              className={`btn-dim btn-white border-right border
                             ${
                               activeButton === timesheetStatus?.findIndex((valueItem) => valueItem === item)
                                 ? "bg-purple btn-lighter text-white"
                                 : "bg-white text-gray"
                             }`}
                              onClick={() => handleButtonClick(item)}
                            >
                              {toCapitalize(item)}
                            </Button>
                          ))}
                      </ButtonGroup>
                    </li>
                    <li className="d-flex align-items-center">
                      Week
                      <label className="switch ms-1 me-1">
                        <input type="checkbox" checked={isSwitchOn} onChange={handleSwitchToggle} />
                        <span className="slider round"></span>
                      </label>
                      ALL
                    </li>
                    {!isSwitchOn && (
                      <li>
                        <DateRangePicker
                          onDateChange={handleDatePickerChange}
                          moveDate={location?.state?.isMoveTimesheet ? { startDate, endDate } : undefined}
                          urlDates={startDate && endDate ? { startDate, endDate } : undefined}
                        />
                      </li>
                    )}

                    {/* {isAllowToAccess(UserPermissionType.timesheet, UserActionType.export) && (
                      <li>
                        <ImportExportButton
                          title={"Export"}
                          onClick={exportTimesheetList}
                          iconName={"arrow-up"}
                          loading={loadingExport}
                        />
                      </li>
                    )} */}
                    <li>
                      <span>
                        <MoreOptions
                          className="text-bg-light"
                          options={[
                            {
                              icon: "bell",
                              text: "Send Notification",
                              action: () => {
                                setNotificationModal(true);
                              },
                            },
                            ...(selectedRows?.length > 0
                              ? [
                                  {
                                    icon: "file-remove-fill",
                                    text: "Cancel",
                                    action: () => {
                                      timesheetRef.current?.cancelTimeSheet();
                                    },
                                    hide:
                                      activeButton === 7 ||
                                      activeButton === 4 ||
                                      !isAllowToAccess(UserPermissionType.timesheet, UserActionType.cancel),
                                  },
                                  {
                                    icon: "file-text",
                                    text: "Move To Wages",
                                    action: () => {
                                      timesheetRef.current?.moveWages();
                                    },
                                    hide:
                                      activeButton !== 4 ||
                                      !isAllowToAccess(UserPermissionType.timesheet, UserActionType.moveToWages),
                                  },
                                  {
                                    icon: "file-remove-fill",
                                    text: "In Query",
                                    action: () => {
                                      timesheetRef.current?.rejectTimeSheet();
                                    },
                                    hide:
                                      activeButton !== 2 ||
                                      !isAllowToAccess(UserPermissionType.timesheet, UserActionType.inquery),
                                  },
                                  {
                                    icon: "file-check-fill",
                                    text: "Verify",
                                    action: () => {
                                      timesheetRef.current?.verifyTimeSheet();
                                    },
                                    hide:
                                      (activeButton !== 1 && activeButton !== 2) ||
                                      !isAllowToAccess(UserPermissionType.timesheet, UserActionType.verify),
                                  },
                                  {
                                    icon: "list-check",
                                    text: "Approve",
                                    action: () => {
                                      timesheetRef.current?.approveTimeSheet();
                                    },
                                    hide:
                                      activeButton !== 3 ||
                                      !isAllowToAccess(UserPermissionType.timesheet, UserActionType.approve),
                                  },
                                ]
                              : []),
                            // Add the Export option here
                            {
                              icon: "arrow-up",
                              text: "Export",
                              action: exportTimesheetList,
                              hide: !isAllowToAccess(UserPermissionType.timesheet, UserActionType.export),
                            },
                          ]}
                        />
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <div className="m-n4">
          <Timesheets
            ref={timesheetRef}
            startDate={startDate}
            endDate={endDate}
            page={currentPage}
            setPage={setCurrentPage}
            clientId={client?.id}
            carerId={carer?.id}
            clientType={clientType?.value}
            searchText={searchText}
            status={status}
            showAll={isSwitchOn}
            getTotalItem={getTotalItem}
            isShowMoreOption={(rows) => setSelectedRows(rows)}
          />
        </div>
      </Content>
      <SendNotificationModal
        visible={notificationModal}
        onClosed={() => setNotificationModal(false)}
        type={SendNotificationType.timesheet}
      />
    </React.Fragment>
  );
};

export default TimesheetNew;
